import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~/app/auth/context/AuthContext';
import { useDashboardPeriodOptions } from '~/app/hooks/dashboard/useDashboardPeriodOptions';
import { defaultDashboardPeriod } from '~/app/services/organization-manager-dashboard/organization-manager-dashboard.period';
import { OperatorScorecardService } from '~/app/services/operator-scorecard/operator-scorecard.service';
import type { DashboardPeriod } from '~/app/services/operator-scorecard/operator-scorecard.types';
import type { ScorecardPageViewProps } from '../ScorecardPage.types';
import {
  SCORECARD_PAGE_SIZE,
  mapFailingSiteRows,
  mapOperatorCards,
  mapSummaryCards,
  paginateRows,
} from '../utils';
import { useFailingSitesColumns } from './useFailingSitesColumns';

export function useScorecardPage(): ScorecardPageViewProps {
  const { t, i18n } = useTranslation('dashboard');
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';
  const periodOptions = useDashboardPeriodOptions();
  const columns = useFailingSitesColumns();
  const [period, setPeriod] = useState<DashboardPeriod>(() =>
    defaultDashboardPeriod(),
  );
  const [page, setPage] = useState(1);

  const statsQuery = useQuery({
    queryKey: ['operator-scorecard', 'stats', organizationId, period],
    queryFn: () => OperatorScorecardService.getStats(organizationId, period),
    enabled: !!organizationId,
  });

  const stats = statsQuery.data;
  const hasOrganization = !!organizationId;

  const summaryCards = useMemo(
    () => (stats ? mapSummaryCards(stats, t) : []),
    [stats, t],
  );

  const operators = useMemo(
    () => (stats ? mapOperatorCards(stats, i18n.language, t) : []),
    [stats, i18n.language, t],
  );

  const allFailingRows = useMemo(
    () => (stats ? mapFailingSiteRows(stats, t) : []),
    [stats, t],
  );

  const pageCount = Math.max(
    1,
    Math.ceil(allFailingRows.length / SCORECARD_PAGE_SIZE),
  );
  const safePage = Math.min(page, pageCount);
  const failingSitesRows = useMemo(
    () => paginateRows(allFailingRows, safePage, SCORECARD_PAGE_SIZE),
    [allFailingRows, safePage],
  );

  const onPeriodChange = useCallback((next: DashboardPeriod) => {
    setPeriod(next);
    setPage(1);
  }, []);

  const onGenerateReport = useCallback(() => {
    // Report export lands in a follow-up ticket.
  }, []);

  return {
    isLoading: hasOrganization && statsQuery.isLoading,
    isError: !hasOrganization || statsQuery.isError,
    errorTitle: t('errors.loadTitle'),
    errorMessage: !hasOrganization
      ? t('errors.missingOrganization')
      : statsQuery.error instanceof Error
        ? statsQuery.error.message
        : t('errors.loadFallback'),
    pageTitle: t('scorecard.title'),
    subtitle: t('scorecard.subtitle'),
    period,
    periodOptions,
    periodAriaLabel: t('header.period.ariaLabel'),
    generateReportLabel: t('scorecard.generateReport'),
    summaryCards,
    operators,
    failingSitesTitle: t('scorecard.failingSites.title'),
    failingSitesUrgentNote: t('scorecard.failingSites.urgentNote', {
      count: stats?.urgentSiteCount ?? 0,
    }),
    failingSitesColumns: columns,
    failingSitesRows,
    showingLabel: t('scorecard.failingSites.showing', {
      shown: failingSitesRows.length,
      total: allFailingRows.length,
    }),
    pageLabel: t('scorecard.failingSites.page', {
      page: safePage,
      pageCount,
    }),
    prevPageAriaLabel: t('portfolioPage.prevPage'),
    nextPageAriaLabel: t('portfolioPage.nextPage'),
    page: safePage,
    pageCount,
    onPageChange: setPage,
    onPeriodChange,
    onGenerateReport,
  };
}
